import { createPortal } from "react-dom";
import { LoaderCircle, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  apiClient,
  type BulkPatchWire,
  type BulkTargetBrand,
  type BulkTargetSummary,
  type KeyMessage,
  type Spokesperson,
  type Theme
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { usePolarityChoices } from "@/shared/components/polarity-choices";
import { useDismiss } from "@/shared/hooks/useDismiss";
import { APPROACH_CHOICES } from "@/features/mentions/mention-filter-criteria";
import { CatalogMultiSelect } from "@/features/mentions/MentionClassificationEditor";
import {
  KEEP,
  buildBulkPatch,
  bulkPatchSize,
  emptyBulkDialogState,
  highlightOptions,
  isBulkPatchEmpty,
  type BulkDialogState,
  type ListField
} from "@/features/mentions/bulk-patch";
import { Select } from "@/shared/components/Select";

/**
 * O editor de classificação EM LOTE (2026-09-21, pedido da galera de
 * negócio).
 *
 * Difere do editor individual em duas coisas, e as duas vêm de o alvo
 * ser MUITAS menções: (1) cada dimensão começa em "não mexer" — o lote
 * não tem valor atual pra pré-preencher, e um select que já viesse com
 * "Positivo" aplicaria uma correção que ninguém pediu; (2) as listas
 * têm MODO (adicionar, remover, substituir), porque cada menção tem a
 * sua lista e "a lista final" não existe aqui. A tradução do modo é do
 * servidor, por menção. O patch em si é montado por `buildBulkPatch`,
 * que é puro e testado.
 *
 * **Subtemas, mensagens-chave e porta-vozes só se corrigem em lote
 * quando TODAS as menções do alvo são da MESMA marca** (decisão da
 * dona, 2026-10-31): esses três são cadastro por marca, e atribuir o
 * da marca A a uma menção da marca B seria erro. Quem diz de que
 * marcas é o servidor, sobre o mesmo alvo do envio (`loadTarget`); com
 * mais de uma, as quatro dimensões travam em "não mexer"; com uma,
 * carrega-se SÓ o catálogo dela, do dono dela (empresa ou concorrente).
 *
 * **"Subtema em destaque" só oferece subtema que TODAS as menções têm**
 * (decisão da dona, 2026-10-03), e a conta acompanha o que o próprio
 * lote faz com os subtemas (`highlightOptions`).
 */
export function BulkClassificationDialog({
  token,
  count,
  targetLabel,
  loadTarget,
  onSubmit,
  onClose
}: {
  token: string;
  /** `null` = "todas do filtro" com o total ainda sendo calculado. */
  count: number | null;
  targetLabel: string;
  /** As marcas das menções do alvo e os subtemas presentes em todas. */
  loadTarget: () => Promise<BulkTargetSummary>;
  onSubmit: (patch: BulkPatchWire) => Promise<void>;
  onClose: () => void;
}) {
  const [state, setState] = useState<BulkDialogState>(emptyBulkDialogState);
  const polarityChoices = usePolarityChoices();
  const [themes, setThemes] = useState<Theme[]>([]);
  // id do grupo -> nome (título dos subtemas).
  const [themeGroupNames, setThemeGroupNames] = useState<Map<number, string>>(
    new Map()
  );
  const [keyMessages, setKeyMessages] = useState<KeyMessage[]>([]);
  const [spokespersons, setSpokespersons] = useState<Spokesperson[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `null` = ainda conferindo. Uma vez, ao abrir: o alvo não muda com o
  // diálogo aberto, e a função do pai é recriada a cada render dele.
  const [target, setTarget] = useState<BulkTargetSummary | null>(null);
  const [targetError, setTargetError] = useState<string | null>(null);
  const loadTargetRef = useRef(loadTarget);

  useEffect(() => {
    let alive = true;
    loadTargetRef
      .current()
      .then(found => {
        if (alive) setTarget(found);
      })
      .catch(failure => {
        if (!alive) return;
        setTargetError(
          errorMessage(failure, "Não foi possível conferir de que marcas são as menções.")
        );
      });
    return () => {
      alive = false;
    };
  }, []);

  useDismiss(true, () => {
    if (!saving) onClose();
  });

  const brands: BulkTargetBrand[] = target?.brands ?? [];
  const singleBrand = brands.length === 1 ? brands[0] : null;
  const brandId = singleBrand?.brand_id ?? null;
  const competitorId = singleBrand?.competitor_id ?? null;
  // As listas só existem quando o alvo é de UMA marca.
  const listsLocked = singleBrand === null;

  useEffect(() => {
    if (brandId === null) return;
    let alive = true;
    void (async () => {
      try {
        const api = apiClient.onCompetitor(competitorId);
        const [t, k, s, g] = await Promise.all([
          api.listThemes(token),
          api.listKeyMessages(token),
          api.listSpokespersons(token),
          // O grupo é só o título: falhar não derruba o catálogo.
          api.listThemeGroups(token).catch(() => [])
        ]);
        if (!alive) return;
        setThemes(t.filter(item => item.brand_id === brandId));
        setKeyMessages(k.filter(item => item.brand_id === brandId));
        setSpokespersons(s.filter(item => item.brand_id === brandId));
        setThemeGroupNames(new Map(g.map(group => [group.id, group.name] as const)));
      } catch (failure) {
        if (!alive) return;
        setCatalogError(
          errorMessage(failure, "Não foi possível carregar o catálogo da marca.")
        );
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, brandId, competitorId]);

  const brandsNote = (() => {
    if (brands.length < 2) return null;
    const names = brands.map(brand => brand.brand_name);
    const shown = names.slice(0, 5).join(", ");
    const more = names.length > 5 ? ` e mais ${names.length - 5}` : "";
    return `As menções escolhidas são de ${brands.length} marcas (${shown}${more}): subtemas, mensagens-chave e porta-vozes só se corrigem em lote quando todas são da mesma marca. Filtre por marca para corrigi-los.`;
  })();
  const catalogNote = singleBrand
    ? singleBrand.competitor_id === null
      ? `Catálogo da marca ${singleBrand.brand_name}.`
      : `Catálogo da marca ${singleBrand.brand_name} · Concorrente ${singleBrand.competitor_name ?? ""}.`
    : null;
  const lockedNote =
    target === null && targetError === null
      ? "Conferindo de que marcas são as menções…"
      : (targetError ?? brandsNote);

  // O destaque escolhido só vale enquanto continuar entre as opções:
  // se a pessoa escolhe um tema e depois manda o lote REMOVÊ-LO, a
  // escolha cai sozinha em vez de virar um lote inteiro de falhas.
  const highlightChoices = highlightOptions(target?.common_themes ?? [], state.themes, themes);
  const highlight =
    highlightChoices.find(option => option.name === state.highlight?.name) ?? null;
  const patch = buildBulkPatch({ ...state, highlight });
  const size = bulkPatchSize(patch);

  const setScalar = (field: "polarity" | "approach", raw: string) =>
    setState(current => ({
      ...current,
      [field]:
        raw === KEEP
          ? { touched: false, value: null }
          : { touched: true, value: raw || null }
    }));

  const setList = (
    field: "themes" | "keyMessages" | "spokespersons",
    update: (current: ListField) => ListField
  ) => setState(current => ({ ...current, [field]: update(current[field]) }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(patch);
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível aplicar a correção."));
      setSaving(false);
    }
  };

  const listField = (
    field: "themes" | "keyMessages" | "spokespersons",
    label: string,
    options: Array<{ id: number; name: string; group?: string | null }>
  ) => {
    const current = state[field];
    return (
      <div className="bulk-list-field">
        <div className="classification-editor-field">
          <span>{label}</span>
          <Select
            value={current.mode}
            disabled={listsLocked}
            onChange={event =>
              setList(field, item => ({
                ...item,
                mode: event.target.value as ListField["mode"]
              }))
            }
          >
            <option value="keep">Não mexer</option>
            <option value="add">Adicionar a todas</option>
            <option value="remove">Remover de todas</option>
            <option value="replace">Substituir em todas por…</option>
          </Select>
        </div>
        {!listsLocked && current.mode !== "keep" &&
          (options.length ? (
            <CatalogMultiSelect
              label={label}
              options={options}
              selected={current.ids}
              onToggle={id =>
                setList(field, item => ({
                  ...item,
                  ids: item.ids.includes(id)
                    ? item.ids.filter(value => value !== id)
                    : [...item.ids, id]
                }))
              }
              onClear={() => setList(field, item => ({ ...item, ids: [] }))}
            />
          ) : (
            <p className="mention-detail-note">
              {catalogError ?? `Nenhum item de ${label.toLowerCase()} cadastrado.`}
            </p>
          ))}
        {current.mode === "replace" && current.ids.length === 0 && (
          <p className="mention-detail-note">
            Substituir por nenhum item ESVAZIA {label.toLowerCase()} em todas
            as menções do lote.
          </p>
        )}
      </div>
    );
  };

  return createPortal(
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div
        className="modal-container bulk-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-dialog-title"
        onClick={event => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">EDIÇÃO EM LOTE</span>
            <h2 id="bulk-dialog-title">
              {count === null
                ? "Corrigir as menções do filtro"
                : `Corrigir ${count.toLocaleString("pt-BR")} ${count === 1 ? "menção" : "menções"}`}
            </h2>
            <p className="bulk-dialog-target">{targetLabel}</p>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            disabled={saving}
          >
            <X size={18} />
          </button>
        </header>
        <div className="modal-body">
          <div className="classification-editor">
            <div className="classification-editor-field">
              <span>Polaridade</span>
              <Select
                value={state.polarity.touched ? (state.polarity.value ?? "") : KEEP}
                onChange={event => setScalar("polarity", event.target.value)}
              >
                <option value={KEEP}>Não mexer</option>
                <option value="">Sem classificação</option>
                {polarityChoices.map(option => (
                  <option key={option.name} value={option.name}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="classification-editor-field">
              <span>Abordagem</span>
              <Select
                value={state.approach.touched ? (state.approach.value ?? "") : KEEP}
                onChange={event => setScalar("approach", event.target.value)}
              >
                <option value={KEEP}>Não mexer</option>
                <option value="">Sem classificação</option>
                {APPROACH_CHOICES.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="classification-editor-field">
              <span>Revisão</span>
              <Select
                value={state.reviewed}
                onChange={event =>
                  setState(current => ({
                    ...current,
                    reviewed: event.target.value as "keep" | "yes" | "no"
                  }))
                }
              >
                <option value="keep">Não mexer</option>
                <option value="yes">Marcar como revisada</option>
                <option value="no">Desmarcar revisão</option>
              </Select>
            </div>
            {lockedNote && (
              <p className="mention-detail-note" role="status">
                {lockedNote}
              </p>
            )}
            {catalogNote && (
              <p className="mention-detail-note">{catalogNote}</p>
            )}
            {listField(
              "themes",
              "Subtemas",
              themes.map(theme => ({
                id: theme.id,
                name: theme.name,
                group:
                  theme.theme_group_id == null
                    ? null
                    : (themeGroupNames.get(theme.theme_group_id) ?? null)
              }))
            )}
            <div className="bulk-list-field">
              <div className="classification-editor-field">
                <span>Subtema em destaque</span>
                <Select
                  value={highlight?.name ?? KEEP}
                  disabled={listsLocked || highlightChoices.length === 0}
                  onChange={event =>
                    setState(current => ({
                      ...current,
                      highlight:
                        highlightChoices.find(
                          option => option.name === event.target.value
                        ) ?? null
                    }))
                  }
                >
                  <option value={KEEP}>Não mexer</option>
                  {highlightChoices.map(option => (
                    <option key={option.name} value={option.name}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              </div>
              <p className="mention-detail-note">
                {listsLocked
                  ? "Disponível quando todas as menções são de uma mesma marca."
                  : highlightChoices.length === 0
                    ? "Nenhum subtema está em todas as menções escolhidas, então não há destaque para definir em lote. Adicione um subtema a todas, acima, para poder destacá-lo."
                    : "Só aparecem os subtemas presentes em todas as menções escolhidas."}
              </p>
            </div>
            {listField(
              "keyMessages",
              "Mensagens-chave",
              keyMessages.map(item => ({ id: item.id, name: item.message }))
            )}
            {listField(
              "spokespersons",
              "Porta-vozes",
              spokespersons.map(item => ({ id: item.id, name: item.name }))
            )}
            <p className="mention-detail-note">
              Cada menção recebe a própria linha de histórico e o recálculo
              do CENA, como na correção individual. Menção que já está como
              o lote pede não muda nem deixa rastro.
            </p>
            {error && (
              <div className="account-feedback error" aria-live="polite">
                {error}
              </div>
            )}
          </div>
        </div>
        <footer className="modal-footer">
          <button
            type="button"
            className="secondary-button"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => void submit()}
            disabled={saving || isBulkPatchEmpty(patch)}
          >
            {saving ? (
              <LoaderCircle className="spin" size={16} />
            ) : size === 0 ? (
              "Escolha o que corrigir"
            ) : (
              `Aplicar ${size} ${size === 1 ? "correção" : "correções"}`
            )}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
